import { useCallback, useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { MdmShell, Field, StatusBadge, ErrorAlert, s } from './_mdmKit.js';
import { Button } from '../../../../shared/components/index.js';

// M05 §7/§8 (T064) — TAT (Turn-Around-Time) Rule Master. CRUD over the existing
// backend `@Controller('v1/master/tat-rules')`:
//   GET  /master/tat-rules?status → T064[]  (plain array, no pagination)
//   POST /master/tat-rules (CreateTatDto) · PUT /master/tat-rules/:id
// NOTE: the backend intentionally exposes NO deactivate endpoint for TAT rules,
// so this screen offers create + edit only. Maker-checker governed (default
// status PENDING_APPROVAL).

interface Tat {
  readonly tatId: string;
  readonly tatCode: string;
  readonly workflowName: string;
  readonly stepName: string;
  readonly responsibleRoleCode: string;
  readonly maxDays: number;
  readonly reminderDay1: number | null;
  readonly reminderDay2: number | null;
  readonly escalationRoleCode: string | null;
  readonly autoCancelFlag: boolean;
  readonly recordStatusCode: string;
}

const STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED', 'PENDING_APPROVAL', 'REJECTED'] as const;

const EMPTY = {
  tatCode: '', workflowName: '', stepName: '', responsibleRoleCode: '', maxDays: '',
  reminderDay1: '', reminderDay2: '', escalationRoleCode: '', autoCancelFlag: false,
};
type Form = typeof EMPTY;

export function TatMasterPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<Tat[] | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Form>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString();
      const r = await apiRequest<Tat[]>(`/master/tat-rules${qs ? `?${qs}` : ''}`, { token });
      setRows(Array.isArray(r) ? r : []);
      setError(null);
    } catch (e) { setError((e as Error).message); setRows([]); }
  }, [token, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const beginCreate = () => { setForm(EMPTY); setEditingId(null); setFormError(null); setShowForm(true); };
  const beginEdit = (row: Tat) => {
    // The list returns full entities, so the row already has every field.
    setForm({
      tatCode: row.tatCode, workflowName: row.workflowName, stepName: row.stepName,
      responsibleRoleCode: row.responsibleRoleCode, maxDays: String(row.maxDays),
      reminderDay1: row.reminderDay1 == null ? '' : String(row.reminderDay1),
      reminderDay2: row.reminderDay2 == null ? '' : String(row.reminderDay2),
      escalationRoleCode: row.escalationRoleCode ?? '', autoCancelFlag: !!row.autoCancelFlag,
    });
    setEditingId(row.tatId); setFormError(null); setShowForm(true);
  };

  const clientValidate = (): string | null => {
    const md = Number(form.maxDays);
    if (!form.maxDays.trim() || Number.isNaN(md) || md < 1) return t('mdm.tat.maxDaysInvalid');
    for (const [labelKey, val] of [['mdm.tat.reminderDay1', form.reminderDay1], ['mdm.tat.reminderDay2', form.reminderDay2]] as const) {
      if (val.trim()) {
        const n = Number(val);
        if (Number.isNaN(n) || n < 0) return t('mdm.tat.reminderInvalid', { field: t(labelKey) });
      }
    }
    return null;
  };

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const v = clientValidate();
    if (v) { setFormError(v); return; }
    setSaving(true); setFormError(null);
    const trimmed = (x: string) => (x.trim() ? x.trim() : undefined);
    const num = (x: string) => (x.trim() ? Number(x) : undefined);
    try {
      if (editingId) {
        await apiRequest(`/master/tat-rules/${encodeURIComponent(editingId)}`, {
          method: 'PUT', token,
          body: {
            workflowName: form.workflowName.trim(), stepName: form.stepName.trim(),
            responsibleRoleCode: form.responsibleRoleCode.trim(), maxDays: Number(form.maxDays),
            reminderDay1: num(form.reminderDay1), reminderDay2: num(form.reminderDay2),
            escalationRoleCode: trimmed(form.escalationRoleCode), autoCancelFlag: form.autoCancelFlag,
          },
        });
      } else {
        await apiRequest('/master/tat-rules', {
          method: 'POST', token,
          body: {
            tatCode: form.tatCode.trim(), workflowName: form.workflowName.trim(),
            stepName: form.stepName.trim(), responsibleRoleCode: form.responsibleRoleCode.trim(),
            maxDays: Number(form.maxDays), reminderDay1: num(form.reminderDay1),
            reminderDay2: num(form.reminderDay2), escalationRoleCode: trimmed(form.escalationRoleCode),
            autoCancelFlag: form.autoCancelFlag,
          },
        });
      }
      setShowForm(false); setForm(EMPTY); setEditingId(null);
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [editingId, form, token, load]);

  return (
    <MdmShell
      label="tat-master"
      title={t('mdm.tat.title')}
      description={t('mdm.tat.description')}
      actions={<Button type="button" variant="primary" data-testid="new-tat" onClick={beginCreate}>{t('mdm.tat.newTat')}</Button>}
    >
      <form aria-label="tat-filters" onSubmit={(e) => { e.preventDefault(); void load(); }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <Field id="tat-status" label={t('mdm.col.status')}>
          <select id="tat-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={s.input}>
            <option value="">{t('mdm.common.all')}</option>
            {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Button type="submit" variant="secondary" data-testid="apply-filters">{t('mdm.common.search')}</Button>
      </form>

      {showForm && (
        <form onSubmit={onSubmit} aria-label={editingId ? 'edit-tat-form' : 'create-tat-form'} style={s.card}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{editingId ? t('mdm.tat.editTitle') : t('mdm.tat.newTitle')}</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field id="t-tatCode" label={t('mdm.tat.codeReq')}>
              <input id="t-tatCode" value={form.tatCode} onChange={set('tatCode')} required disabled={!!editingId} style={s.input} />
            </Field>
            <Field id="t-workflowName" label={t('mdm.tat.workflowReq')}>
              <input id="t-workflowName" value={form.workflowName} onChange={set('workflowName')} required style={s.input} />
            </Field>
            <Field id="t-stepName" label={t('mdm.tat.stepReq')}>
              <input id="t-stepName" value={form.stepName} onChange={set('stepName')} required style={s.input} />
            </Field>
            <Field id="t-responsibleRoleCode" label={t('mdm.tat.roleReq')}>
              <input id="t-responsibleRoleCode" value={form.responsibleRoleCode} onChange={set('responsibleRoleCode')} required style={s.input} />
            </Field>
            <Field id="t-maxDays" label={t('mdm.tat.maxDaysReq')} hint={t('mdm.tat.maxDaysHint')}>
              <input id="t-maxDays" type="number" value={form.maxDays} onChange={set('maxDays')} required style={s.input} />
            </Field>
            <Field id="t-reminderDay1" label={t('mdm.tat.reminderDay1')}>
              <input id="t-reminderDay1" type="number" min={0} value={form.reminderDay1} onChange={set('reminderDay1')} style={s.input} />
            </Field>
            <Field id="t-reminderDay2" label={t('mdm.tat.reminderDay2')}>
              <input id="t-reminderDay2" type="number" min={0} value={form.reminderDay2} onChange={set('reminderDay2')} style={s.input} />
            </Field>
            <Field id="t-escalationRoleCode" label={t('mdm.tat.escalationRole')}>
              <input id="t-escalationRoleCode" value={form.escalationRoleCode} onChange={set('escalationRoleCode')} style={s.input} />
            </Field>
            <Field id="t-autoCancelFlag" label={t('mdm.tat.autoCancelLabel')}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                <input id="t-autoCancelFlag" type="checkbox" checked={form.autoCancelFlag}
                       onChange={(e) => setForm((f) => ({ ...f, autoCancelFlag: e.target.checked }))} />
                {t('mdm.tat.autoCancel')}
              </label>
            </Field>
          </div>
          <ErrorAlert message={formError} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="submit" variant="primary" data-testid="save-tat" disabled={saving}>
              {saving ? t('mdm.common.saving') : editingId ? t('mdm.common.saveChanges') : t('mdm.tat.createTat')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
          </div>
        </form>
      )}

      <ErrorAlert message={error} />
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="tat-empty" style={{ color: s.muted }}>{t('mdm.tat.empty')}</p>
      ) : (
        <DataGrid<any>
          caption="tat-table"
          rows={rows}
          getRowId={(row) => row.tatId}
          rowTestId={(row) => `tat-row-${row.tatId}`}
          columns={[
            { key: 'tatCode', header: t('mdm.tat.colTatCode'), value: (row) => row.tatCode },
            { key: 'workflow', header: t('mdm.tat.colWorkflow'), value: (row) => row.workflowName },
            { key: 'step', header: t('mdm.tat.colStep'), value: (row) => row.stepName },
            { key: 'role', header: t('mdm.tat.colRole'), value: (row) => row.responsibleRoleCode },
            { key: 'maxDays', header: t('mdm.tat.colMaxDays'), value: (row) => row.maxDays, numeric: true },
            { key: 'reminders', header: t('mdm.tat.colReminders'), value: (row) => [row.reminderDay1, row.reminderDay2].filter((x: unknown) => x != null).join(' / ') || '—' },
            { key: 'escalation', header: t('mdm.tat.colEscalation'), value: (row) => row.escalationRoleCode ?? '—' },
            { key: 'autoCancel', header: t('mdm.tat.colAutoCancel'), value: (row) => (row.autoCancelFlag ? t('mdm.common.yes') : t('mdm.common.no')) },
            { key: 'status', header: t('mdm.col.status'), value: (row) => row.recordStatusCode, render: (row) => <StatusBadge status={row.recordStatusCode} /> },
          ]}
          actionsHeader={t('mdm.col.actions')}
          actions={(row) => (
            <Button type="button" variant="secondary" data-testid={`edit-${row.tatId}`} onClick={() => beginEdit(row)}>{t('mdm.common.edit')}</Button>
          )}
        />
      )}
    </MdmShell>
  );
}
